import { useState, useEffect } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, AlertTriangle, Clock, CheckCircle2, Filter, RefreshCw, Loader2, X, Archive, Settings2, Download, RotateCcw } from 'lucide-react';

import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Labels de tipos de alerta
const ALERT_TYPE_LABELS = {
  VIGENCIA: 'Renovación',
  RENEWAL: 'Renovación',
  EXPIRY: 'Renovación',
  DECLARATION_OF_USE: 'Declaración de uso',
  CUSTOM: 'Personalizada'
};

// Tipos visibles en filtros
const VISIBLE_ALERT_TYPES = [
  { value: 'VIGENCIA', label: 'Renovación' },
  { value: 'DECLARATION_OF_USE', label: 'Declaración de uso' }
];


const ALERT_STATUS_LABELS = {
  PENDING: 'Pendiente',
  NOTIFIED: 'Notificada',
  COMPLETED: 'Completada',
  DISMISSED: 'Descartada',
  ARCHIVED: 'Archivada'
};

// Opciones de días para preferencias - Antes del vencimiento
const DAYS_BEFORE_OPTIONS = [
  { value: 270, label: '9 meses' },
  { value: 180, label: '6 meses' },
  { value: 90, label: '3 meses' },
  { value: 30, label: '1 mes' },
];

// Opciones de días para preferencias - Después del vencimiento
const DAYS_AFTER_OPTIONS = [
  { value: 180, label: '6 meses' },
  { value: 90, label: '3 meses' },
  { value: 30, label: '1 mes' },
];

// Legacy alias
const DAYS_OPTIONS = DAYS_BEFORE_OPTIONS;

export const Alerts = () => {
  const { token, isCliente, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [deduplicating, setDeduplicating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    alert_type: '',
    status: 'PENDING',
    is_overdue: ''
  });

  // KPIs
  const [kpis, setKpis] = useState({
    pending: 0,
    overdue: 0,
    next_30_days: 0,
    completed_this_month: 0
  });

  // Action dialog
  const [actionDialog, setActionDialog] = useState({ open: false, alert: null, action: '' });

  // User Alert Preferences - ÉPICA 3.2.1
  const [prefsModalOpen, setPrefsModalOpen] = useState(false);
  const [userPrefs, setUserPrefs] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filters, showArchived]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.alert_type) params.append('alert_type', filters.alert_type);
      if (filters.status && !showArchived) params.append('status', filters.status);
      if (showArchived) params.append('status', 'ARCHIVED');
      if (filters.is_overdue === 'true') params.append('is_overdue', 'true');
      if (filters.is_overdue === 'false') params.append('is_overdue', 'false');

      const [alertsRes, kpisRes] = await Promise.all([
        axios.get(`${API}/alerts?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/alerts/kpis`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setAlerts(alertsRes.data);
      setKpis(kpisRes.data);
    } catch (error) {
      console.error('Error fetching alerts:', error);
      toast.error('Error al cargar alertas');
    } finally {
      setLoading(false);
    }
  };

  // ===== USER ALERT PREFERENCES =====
  const handleOpenPrefsModal = async () => {
    setPrefsModalOpen(true);
    try {
      const res = await axios.get(`${API}/users/me/alert-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns flat object (not array)
      setUserPrefs(res.data);
    } catch (error) {
      console.error('Error fetching preferences:', error);
      toast.error('Error al cargar preferencias');
    }
  };

  const handleSavePrefs = async () => {
    setSavingPrefs(true);
    try {
      await axios.put(`${API}/users/me/alert-preferences`, userPrefs, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Preferencias guardadas');
      setPrefsModalOpen(false);
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Error al guardar preferencias');
    } finally {
      setSavingPrefs(false);
    }
  };

  // Export alerts - ÉPICA 3.3
  const handleExportAlerts = () => {
    const headers = ['Tipo', 'Marca', 'Expediente', 'Fecha Límite', 'Estado', 'Días Restantes'];
    const rows = alerts.map(alert => [
      ALERT_TYPE_LABELS[alert.alert_type] || alert.alert_type,
      alert.trademark_denominacion || 'Sin denominación',
      alert.trademark_expediente || '',
      alert.due_date ? new Date(alert.due_date).toLocaleDateString('es-MX') : 'N/A',
      ALERT_STATUS_LABELS[alert.status] || alert.status,
      alert.days_until_due !== undefined ? alert.days_until_due : 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `alertas_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Exportación completada');
  };

  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      const response = await axios.post(`${API}/alerts/recompute`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Alertas recalculadas', {
        description: `${response.data.alerts_created ?? 0} nuevas, ${response.data.alerts_marked_overdue ?? 0} vencidas`
      });

      await fetchData();
    } catch (error) {
      console.error('Error recomputing alerts:', error);
      toast.error('Error al recalcular alertas', {
        description: error.response?.data?.detail || 'Verifica que tienes permisos de administrador'
      });
    } finally {
      setRecomputing(false);
    }
  };

  // ÉPICA 3.4: Eliminar alertas duplicadas
  const handleDeduplicate = async () => {
    setDeduplicating(true);
    try {
      const response = await axios.post(`${API}/alerts/deduplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Duplicados eliminados', {
        description: `Se eliminaron ${response.data.deleted_count} alertas duplicadas`
      });

      await fetchData();
    } catch (error) {
      console.error('Error deduplicating alerts:', error);
      toast.error('Error al eliminar duplicados');
    } finally {
      setDeduplicating(false);
    }
  };

  const handleAction = async () => {
    if (!actionDialog.alert) return;
    setActionLoading(true);
    // Optimistic: remove from list immediately
    setAlerts(prev => prev.filter(a => a.id !== actionDialog.alert.id));
    const savedDialog = { ...actionDialog };
    setActionDialog({ open: false, alert: null, action: '' });
    try {
      await axios.put(`${API}/alerts/${savedDialog.alert.id}`, {
        status: savedDialog.action
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(savedDialog.action === 'COMPLETED' ? 'Alerta completada ✓' : 'Alerta descartada');
      // Refresh KPIs silently
      axios.get(`${API}/alerts/kpis`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setKpis(r.data)).catch(() => { });
    } catch (error) {
      console.error('Error updating alert:', error);
      toast.error('Error al actualizar alerta');
      await fetchData(); // revert on error
    } finally {
      setActionLoading(false);
    }
  };

  const openActionDialog = (alert, action) => {
    setActionDialog({ open: true, alert, action });
  };

  // Color del badge basado en is_overdue:
  //   Amarillo = pre-window (≤60 días antes, no vencida)
  //   Rojo     = post-window (vencida, ≤60 días después)
  const getAlertBadgeColor = (isOverdue) => {
    return isOverdue
      ? 'bg-red-100 text-red-700 border-red-200'       // Vencida → rojo
      : 'bg-amber-100 text-amber-700 border-amber-200'; // Pendiente → amarillo
  };

  // Mantener por compatibilidad con código existente
  const getAlertTypeColor = (_type, isOverdue = false) => getAlertBadgeColor(isOverdue);

  const getDaysRemainingColor = (days, isOverdue) => {
    if (isOverdue) return 'text-red-600 font-semibold';
    if (days <= 15) return 'text-red-500';
    if (days <= 30) return 'text-amber-600';
    if (days <= 60) return 'text-amber-500';
    return 'text-gray-600';
  };


  const formatDueDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Group alerts — vencidas / próximos 90d / próximamente
  const overdueAlerts = alerts.filter(a => a.is_overdue);
  const next90Days = alerts.filter(a => !a.is_overdue && a.days_remaining <= 90);
  const laterAlerts = alerts.filter(a => !a.is_overdue && a.days_remaining > 90);

  // Sorting Logic - Reused from Trademarks/ClientDetail
  const [sortConfig, setSortConfig] = useState({ key: 'days_remaining', direction: 'asc' });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortAlerts = (list) => {
    return [...list].sort((a, b) => {
      if (!sortConfig.key) return 0;

      let aValue = a[sortConfig.key] || '';
      let bValue = b[sortConfig.key] || '';

      // Mappings for specific columns
      if (sortConfig.key === 'trademark') {
        aValue = a.trademark_denominacion || a.trademark_expediente || '';
        bValue = b.trademark_denominacion || b.trademark_expediente || '';
      }
      else if (sortConfig.key === 'client') {
        aValue = a.client_nombre || '';
        bValue = b.client_nombre || '';
      }
      else if (sortConfig.key === 'days_remaining' || sortConfig.key === 'due_date') {
        // Numeric/Date sort
        aValue = new Date(a.due_date).getTime();
        bValue = new Date(b.due_date).getTime();
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <div className="space-y-6 pb-20">
      {/* Header - Balanced for mobile */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">Alertas</h2>
          <p className="text-xs md:text-sm text-gray-500 mt-0.5">Gestión de vencimientos y renovaciones</p>
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar shrink-0">
          {isAdmin() && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDeduplicate}
              disabled={deduplicating}
              className="border-gray-300 shrink-0 h-8 text-xs"
            >
              {deduplicating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5 mr-1.5" />}
              <span className="hidden sm:inline">Eliminar duplicados</span>
              <span className="sm:hidden">Duplicados</span>
            </Button>
          )}
          {/* Recalcular visible para todos */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRecompute}
            disabled={recomputing}
            className="border-gray-300 shrink-0 h-8 text-xs"
          >
            {recomputing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Recalcular
          </Button>
          {alerts.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportAlerts}
              className="border-gray-300 shrink-0 h-8 text-xs"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              <span className="hidden sm:inline">Exportar ({alerts.length})</span>
              <span className="sm:hidden">Export</span>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenPrefsModal}
            className="border-gray-300 shrink-0 h-8 text-xs"
          >
            <Settings2 className="h-3.5 w-3.5 mr-1.5" />
            <span className="hidden sm:inline">Preferencias</span>
            <span className="sm:hidden">Prefs</span>
          </Button>
        </div>
      </div>

      {/* Info banner - auto recalculate */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-2.5 flex items-center gap-3 text-xs text-blue-700">
        <RefreshCw className="h-3.5 w-3.5 shrink-0 text-blue-500" />
        <span>
          Las alertas se recalculan <strong>automáticamente cada día a las 3 AM</strong>. Si acabas de agregar nuevas marcas, usa “Recalcular” para actualizar ahora.
        </span>
      </div>

      {/* KPI Cards - Responsive Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-blue-50/50 rounded-lg">
              <Bell className="h-4 w-4 text-blue-600" />
            </div>
            <p className="text-[10px] text-gray-500 font-bold uppercase">Pendientes</p>
          </div>
          <p className="text-xl md:text-2xl font-bold text-gray-900 mt-1">{kpis.pending}</p>
        </div>
        <div className="bg-white rounded-lg border border-red-100 p-3 shadow-sm">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-red-50/50 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-red-600" />
            </div>
            <p className="text-[10px] text-red-600 font-bold uppercase">Vencidas</p>
          </div>
          <p className="text-xl md:text-2xl font-bold text-red-600 mt-1">{kpis.overdue}</p>
        </div>
        <div className="bg-white rounded-lg border border-indigo-100 p-3 shadow-sm">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-indigo-50/50 rounded-lg">
              <Clock className="h-4 w-4 text-indigo-600" />
            </div>
            <p className="text-[10px] text-indigo-600 font-bold uppercase">Próx. 90d</p>
          </div>
          <p className="text-xl md:text-2xl font-bold text-indigo-900 mt-1">{kpis.next_90_days ?? kpis.next_30_days}</p>
        </div>
        <div className="bg-white rounded-lg border border-green-100 p-3 shadow-sm">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-green-50/50 rounded-lg">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            </div>
            <p className="text-[10px] text-green-600 font-bold uppercase">Mes</p>
          </div>
          <p className="text-xl md:text-2xl font-bold text-green-700 mt-1">{kpis.completed_this_month}</p>
        </div>
      </div>

      {/* Filters - Stack on mobile */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <Select
            value={filters.alert_type || "all"}
            onValueChange={(value) => setFilters({ ...filters, alert_type: value === "all" ? "" : value })}
          >
            <SelectTrigger className="h-10 text-xs text-gray-900 bg-white">
              <SelectValue placeholder="Tipo de alerta" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {VISIBLE_ALERT_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.status || "all"}
            onValueChange={(value) => setFilters({ ...filters, status: value === "all" ? "" : value })}
          >
            <SelectTrigger className="h-10 text-xs text-gray-900 bg-white">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="PENDING">Pendiente</SelectItem>
              <SelectItem value="COMPLETED">Completada</SelectItem>
              <SelectItem value="DISMISSED">Descartada</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.is_overdue || "all"}
            onValueChange={(value) => setFilters({ ...filters, is_overdue: value === "all" ? "" : value })}
          >
            <SelectTrigger className="h-10 text-xs text-gray-900 bg-white">
              <SelectValue placeholder="Vencimiento" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="true">Solo vencidas</SelectItem>
              <SelectItem value="false">No vencidas</SelectItem>
            </SelectContent>
          </Select>

          {(filters.alert_type || (filters.status && filters.status !== 'PENDING') || filters.is_overdue) && (
            <Button
              variant="ghost"
              onClick={() => setFilters({ alert_type: '', status: 'PENDING', is_overdue: '' })}
              className="h-10 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              <RotateCcw className="mr-2 h-3.5 w-3.5" />
              Restablecer filtros
            </Button>
          )}
        </div>

        {/* Show Archived Toggle */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-50">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-gray-50 rounded-lg">
              <Archive className="h-4 w-4 text-gray-400" />
            </div>
            <Label htmlFor="show-archived" className="text-xs text-gray-600">
              Ver alertas archivadas ({'>'}1 año vencidas)
            </Label>
          </div>
          <Switch
            id="show-archived"
            checked={showArchived}
            onCheckedChange={setShowArchived}
          />
        </div>
      </div>

      {/* Alerts List */}
      {loading ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-300 mx-auto" />
        </div>
      ) : alerts.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Bell className="h-10 w-10 text-gray-200 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-500">No hay alertas que mostrar</p>
          <p className="text-xs text-gray-400 mt-1">Prueba a cambiar los filtros o archivar menos alertas</p>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Helper for Alert Actions */}
          {(() => {
            const renderAlertContent = (list, title, icon, colorClass, borderClass, bgClass, headerBgClass) => {
              if (list.length === 0) return null;
              const sortedList = sortAlerts(list);

              return (
                <div className="space-y-3">
                  <h3 className={`text-[10px] font-bold uppercase tracking-wider ${colorClass} flex items-center gap-2 px-1`}>
                    {icon}
                    {title} ({list.length})
                  </h3>

                  {/* Desktop Table View */}
                  <div className={`hidden lg:block bg-white rounded-xl border ${borderClass} overflow-hidden shadow-sm`}>
                    <table className="w-full">
                      <thead className={`${headerBgClass} border-b ${borderClass}`}>
                        <tr>
                          <th className="px-6 py-3 text-left text-[10px] font-bold text-gray-500 uppercase cursor-pointer hover:bg-gray-100/50" onClick={() => requestSort('trademark')}>Marca {getSortIcon('trademark')}</th>
                          <th className="px-6 py-3 text-left text-[10px] font-bold text-gray-500 uppercase cursor-pointer hover:bg-gray-100/50" onClick={() => requestSort('alert_type')}>Tipo {getSortIcon('alert_type')}</th>
                          <th className="px-6 py-3 text-left text-[10px] font-bold text-gray-500 uppercase cursor-pointer hover:bg-gray-100/50" onClick={() => requestSort('due_date')}>Fecha límite {getSortIcon('due_date')}</th>
                          <th className="px-6 py-3 text-left text-[10px] font-bold text-gray-500 uppercase cursor-pointer hover:bg-gray-100/50" onClick={() => requestSort('client')}>Cliente {getSortIcon('client')}</th>
                          {!isCliente() && <th className="px-6 py-3 text-right text-[10px] font-bold text-gray-500 uppercase">Acciones</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {sortedList.map(alert => (
                          <tr key={alert.id} className="hover:bg-gray-50/50 transition-colors">
                            <td className="px-6 py-4">
                              <button onClick={() => navigate(`/trademarks/${alert.trademark_id}`)} className="text-sm font-bold text-gray-900 hover:text-blue-600 transition-colors">
                                {alert.trademark_denominacion || alert.trademark_expediente || 'S/N'}
                              </button>
                            </td>
                            <td className="px-6 py-4">
                              <Badge className={`${getAlertTypeColor(alert.alert_type, alert.is_overdue)} border shadow-none text-[10px]`}>
                                {ALERT_TYPE_LABELS[alert.alert_type] || alert.alert_type}
                              </Badge>
                            </td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-600">
                              {formatDueDate(alert.due_date)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {alert.client_nombre || '-'}
                            </td>
                            {!isCliente() && (
                              <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-1.5">
                                  <Button variant="ghost" size="sm" onClick={() => openActionDialog(alert, 'COMPLETED')} className="h-8 text-green-600 hover:text-green-700 hover:bg-green-50 px-2 font-bold text-xs">
                                    Listo
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => openActionDialog(alert, 'DISMISSED')} className="h-8 text-gray-400 hover:text-gray-600 hover:bg-gray-50 px-2 text-xs">
                                    Omitir
                                  </Button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="lg:hidden space-y-3">
                    {sortedList.map(alert => (
                      <div key={alert.id} className={`bg-white rounded-xl border ${borderClass} p-4 shadow-sm active:scale-[0.98] transition-transform`} onClick={() => navigate(`/trademarks/${alert.trademark_id}`)}>
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1 min-w-0 pr-2">
                            <h4 className="text-sm font-bold text-gray-900 truncate">
                              {alert.trademark_denominacion || alert.trademark_expediente || 'S/N'}
                            </h4>
                            <div className="text-[10px] text-gray-400 font-mono mt-0.5">{alert.trademark_expediente}</div>
                          </div>
                          <Badge className={`${getAlertTypeColor(alert.alert_type, alert.is_overdue)} border shadow-none text-[9px] px-1.5 h-4`}>
                            {ALERT_TYPE_LABELS[alert.alert_type] || alert.alert_type}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div className="bg-gray-50/50 p-2 rounded-lg">
                            <span className="text-[9px] text-gray-400 uppercase font-bold block mb-0.5">Vence el</span>
                            <span className={`text-xs font-bold ${alert.is_overdue ? 'text-red-600' : 'text-gray-700'}`}>
                              {formatDueDate(alert.due_date)}
                            </span>
                          </div>
                          <div className="bg-gray-50/50 p-2 rounded-lg">
                            <span className="text-[9px] text-gray-400 uppercase font-bold block mb-0.5">Faltan</span>
                            <span className={`text-xs font-bold ${getDaysRemainingColor(alert.days_remaining, alert.is_overdue)}`}>
                              {alert.is_overdue ? 'Vencida' : `${alert.days_remaining} días`}
                            </span>
                          </div>
                        </div>

                        {!isCliente() && (
                          <div className="flex gap-2 border-t border-gray-50 pt-3">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 h-8 text-[10px] bg-green-50 text-green-700 border-green-100"
                              onClick={(e) => { e.stopPropagation(); openActionDialog(alert, 'COMPLETED'); }}
                            >
                              Completar
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 h-8 text-[10px] bg-gray-50 text-gray-600 border-gray-100"
                              onClick={(e) => { e.stopPropagation(); openActionDialog(alert, 'DISMISSED'); }}
                            >
                              Descartar
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            };

            return (
              <>
                {renderAlertContent(overdueAlerts, "Vencidas", <AlertTriangle className="h-3.5 w-3.5" />, "text-red-600", "border-red-100", "bg-red-50/30", "bg-red-50/50")}
                {renderAlertContent(next90Days, "Próximos 90 días", <Clock className="h-3.5 w-3.5" />, "text-indigo-600", "border-indigo-100", "bg-indigo-50/30", "bg-indigo-50/50")}
                {renderAlertContent(laterAlerts, "Próximamente", <Bell className="h-3.5 w-3.5" />, "text-gray-600", "border-gray-100", "bg-gray-50/30", "bg-gray-50/50")}
              </>
            );
          })()}
        </div>
      )}

      {/* Preferences Modal */}
      <Dialog open={prefsModalOpen} onOpenChange={setPrefsModalOpen}>
        <DialogContent className="sm:max-w-[520px] p-0 overflow-hidden">
          <div className="p-6">
            <DialogHeader className="mb-5">
              <DialogTitle className="text-lg font-bold flex items-center gap-2">
                <Settings2 className="h-5 w-5 text-indigo-600" />
                Preferencias de Alertas
              </DialogTitle>
              <DialogDescription className="text-xs text-gray-500">
                Configura cuántos días antes y después del vencimiento quieres ver alertas, y si deseas recibir un resumen por correo.
              </DialogDescription>
            </DialogHeader>

            {userPrefs ? (
              <div className="space-y-6">
                {/* Renovación / Vigencia */}
                <div className="rounded-xl bg-amber-50/60 border border-amber-100 p-4 space-y-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Bell className="h-4 w-4 text-amber-600" />
                    <span className="text-sm font-bold text-gray-900">Renovación / Vigencia</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] font-bold text-gray-500 uppercase mb-1 block">Meses antes del vencimiento</label>
                      <Select
                        value={String(userPrefs.vigencia_before_days ?? 270)}
                        onValueChange={val => setUserPrefs(p => ({ ...p, vigencia_before_days: parseInt(val) }))}
                      >
                        <SelectTrigger className="h-9 text-xs bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DAYS_BEFORE_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-gray-500 uppercase mb-1 block">Meses después del vencimiento</label>
                      <Select
                        value={String(userPrefs.vigencia_after_days ?? 180)}
                        onValueChange={val => setUserPrefs(p => ({ ...p, vigencia_after_days: parseInt(val) }))}
                      >
                        <SelectTrigger className="h-9 text-xs bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DAYS_AFTER_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Declaración de Uso */}
                <div className="rounded-xl bg-blue-50/60 border border-blue-100 p-4 space-y-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Bell className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-bold text-gray-900">Declaración de Uso</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] font-bold text-gray-500 uppercase mb-1 block">Meses antes</label>
                      <Select
                        value={String(userPrefs.uso_before_days ?? 270)}
                        onValueChange={val => setUserPrefs(p => ({ ...p, uso_before_days: parseInt(val) }))}
                      >
                        <SelectTrigger className="h-9 text-xs bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DAYS_BEFORE_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-gray-500 uppercase mb-1 block">Meses después</label>
                      <Select
                        value={String(userPrefs.uso_after_days ?? 180)}
                        onValueChange={val => setUserPrefs(p => ({ ...p, uso_after_days: parseInt(val) }))}
                      >
                        <SelectTrigger className="h-9 text-xs bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DAYS_AFTER_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Email Notifications */}
                <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-gray-900">Notificaciones por correo</span>
                      <span className="text-[9px] font-bold uppercase bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">Próximamente</span>
                    </div>
                    <Switch
                      checked={userPrefs.email_notifications_enabled ?? false}
                      onCheckedChange={val => setUserPrefs(p => ({ ...p, email_notifications_enabled: val }))}
                    />
                  </div>
                  {userPrefs.email_notifications_enabled && (
                    <div className="pt-1">
                      <label className="text-[10px] font-bold text-gray-500 uppercase mb-2 block">Frecuencia de resumen</label>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { value: 'weekly', label: 'Semanal' },
                          { value: 'biweekly', label: 'Quincenal' },
                          { value: 'monthly', label: 'Mensual' },
                        ].map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => setUserPrefs(p => ({ ...p, email_frequency: opt.value }))}
                            className={`py-2 rounded-lg border text-xs font-bold transition-colors ${(userPrefs.email_frequency ?? 'monthly') === opt.value
                              ? 'bg-indigo-600 text-white border-indigo-600'
                              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                              }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      <p className="text-[10px] text-gray-400 mt-2">
                        Recibirás un correo con el listado completo de alertas activas con la frecuencia seleccionada.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="py-8 flex justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-gray-300" />
              </div>
            )}

            <div className="mt-6 flex gap-3">
              <Button variant="outline" className="flex-1 h-10 text-sm" onClick={() => setPrefsModalOpen(false)}>Cancelar</Button>
              <Button className="flex-1 h-10 text-sm font-bold bg-indigo-600 hover:bg-indigo-700 text-white" onClick={handleSavePrefs} disabled={savingPrefs}>
                {savingPrefs ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Action Confirm Dialog */}
      <AlertDialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ ...actionDialog, open: false })}>
        <AlertDialogContent className="rounded-xl max-w-[400px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-bold">¿Marcar alerta como {actionDialog.action === 'COMPLETED' ? 'completada' : 'descartada'}?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm">
              Esta acción quitará la alerta de tu lista principal de pendientes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-6 gap-3">
            <AlertDialogCancel className="h-10 text-xs font-bold border-gray-200">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAction}
              className={`h-10 text-xs font-bold text-white ${actionDialog.action === 'COMPLETED' ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-900 hover:bg-gray-800'}`}
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};
